import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useFetch, useSiteData } from "../lib/hooks";
import { fileUrl, api, formatApiError } from "../lib/api";
import { Reveal, Overline } from "../components/site/Reveal";
import PageHeader from "../components/site/PageHeader";
import { Check, ArrowRight, ArrowLeft, Loader2, PartyPopper } from "lucide-react";

const IMG = (s, k) => fileUrl(s?.images?.[k]);
const EVENT_TYPES = ["Wedding", "Reception", "Corporate", "Birthday", "Anniversary", "Private Party", "Conference", "Bulk / Daily Meals"];
const SERVICE = ["Buffet", "Plated", "Live Counters", "Food Stations", "Packed Meals", "Corporate Meals", "Bulk Food"];
const MEAL = ["Non-Vegetarian", "Mixed (Veg + Non-Veg)", "Vegetarian"];
const BUDGET = ["Under ₹500/plate", "₹500 – ₹1000", "₹1000 – ₹2000", "₹2000+"];

export default function QuoteBuilder() {
  const s = useSiteData();
  const { data: menu } = useFetch("/public/menu");
  const [step, setStep] = useState(0);
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");
  const [f, setF] = useState({
    event_type: "", guests: "", event_date: "", location: "", meal_preference: "Non-Vegetarian",
    cuisine: "Indian Non-Veg", budget: "", service_type: "", menus: [], name: "", phone: "", email: "",
  });
  const set = (k, v) => setF({ ...f, [k]: v });
  const toggleMenu = (name) => setF({ ...f, menus: f.menus.includes(name) ? f.menus.filter((m) => m !== name) : [...f.menus, name] });

  const steps = ["Event", "Details", "Style", "Menus", "You"];

  const submit = async () => {
    setStatus("loading"); setError("");
    try {
      await api.post("/public/enquiry", {
        ...f, category: "EVENT ENQUIRY", source: "Quote Builder",
        message: `Menu preferences: ${f.menus.join(", ") || "—"}`,
        menu_ref: f.menus.join(", "),
      });
      setStatus("done");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail)); setStatus("idle");
    }
  };

  const next = () => setStep((x) => Math.min(x + 1, 4));
  const prev = () => setStep((x) => Math.max(x - 1, 0));
  const chip = (val, cur, on) => (
    <button key={val} onClick={on} className={`px-5 py-3 rounded-full border text-sm transition-all ${cur ? "bg-burgundy text-ivory border-burgundy" : "border-gold/30 text-charcoal/70 hover:border-gold"}`}>{val}</button>
  );
  const inp = "w-full bg-white border border-gold/25 rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-gold";

  return (
    <div>
      <PageHeader overline="Build Your Menu" title={<>Design your <span className="italic gold-text">event.</span></>}
        subtitle="A few quick choices and our team will craft a custom proposal for you." image={IMG(s, "buffet")} />

      <section className="py-16 sm:py-24 bg-ivory">
        <div className="max-w-3xl mx-auto px-4 sm:px-8">
          {status === "done" ? (
            <Reveal className="text-center py-16">
              <PartyPopper className="w-16 h-16 text-gold mx-auto mb-6" />
              <h2 className="font-serif text-4xl text-burgundy mb-4">Your request is in!</h2>
              <p className="text-charcoal/70 max-w-md mx-auto">Thank you for contacting Foodie Connect Caterer. Our team will get in touch with you shortly with a custom quote.</p>
            </Reveal>
          ) : (
            <>
              {/* progress */}
              <div className="flex items-center justify-between mb-12">
                {steps.map((label, i) => (
                  <div key={label} className="flex-1 flex items-center">
                    <div className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-medium shrink-0 ${i <= step ? "bg-gold text-velvet" : "bg-cream text-charcoal/40 border border-gold/20"}`}>
                      {i < step ? <Check className="w-4 h-4" /> : i + 1}
                    </div>
                    {i < steps.length - 1 && <div className={`h-px flex-1 mx-1 ${i < step ? "bg-gold" : "bg-gold/20"}`} />}
                  </div>
                ))}
              </div>

              <AnimatePresence mode="wait">
                <motion.div key={step} initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -30 }} transition={{ duration: 0.35 }}>
                  {step === 0 && (<div data-testid="qb-step-event">
                    <Overline>What are we catering?</Overline>
                    <h2 className="font-serif text-3xl text-burgundy mt-3 mb-6">Choose your event type</h2>
                    <div className="flex flex-wrap gap-3">{EVENT_TYPES.map((e) => chip(e, f.event_type === e, () => set("event_type", e)))}</div>
                  </div>)}

                  {step === 1 && (<div className="space-y-5" data-testid="qb-step-details">
                    <Overline>The essentials</Overline>
                    <h2 className="font-serif text-3xl text-burgundy">Event details</h2>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <input placeholder="Number of guests" value={f.guests} onChange={(e) => set("guests", e.target.value)} className={inp} data-testid="qb-guests" />
                      <input type="date" value={f.event_date} onChange={(e) => set("event_date", e.target.value)} className={inp} data-testid="qb-date" />
                      <input placeholder="Location / City" value={f.location} onChange={(e) => set("location", e.target.value)} className={inp + " sm:col-span-2"} data-testid="qb-location" />
                    </div>
                    <div><p className="text-sm text-charcoal/60 mb-2">Meal preference</p><div className="flex flex-wrap gap-3">{MEAL.map((m) => chip(m, f.meal_preference === m, () => set("meal_preference", m)))}</div></div>
                  </div>)}

                  {step === 2 && (<div className="space-y-6" data-testid="qb-step-style">
                    <Overline>How should we serve?</Overline>
                    <h2 className="font-serif text-3xl text-burgundy">Service style & budget</h2>
                    <div><p className="text-sm text-charcoal/60 mb-2">Service style</p><div className="flex flex-wrap gap-3">{SERVICE.map((x) => chip(x, f.service_type === x, () => set("service_type", x)))}</div></div>
                    <div><p className="text-sm text-charcoal/60 mb-2">Budget range</p><div className="flex flex-wrap gap-3">{BUDGET.map((x) => chip(x, f.budget === x, () => set("budget", x)))}</div></div>
                  </div>)}

                  {step === 3 && (<div data-testid="qb-step-menus">
                    <Overline>Pick what you love</Overline>
                    <h2 className="font-serif text-3xl text-burgundy mt-3 mb-6">Menu preferences</h2>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                      {menu.map((c) => (
                        <button key={c.id} onClick={() => toggleMenu(c.name)} className={`relative overflow-hidden rounded-xl aspect-[4/3] border-2 transition-all ${f.menus.includes(c.name) ? "border-gold" : "border-transparent"}`}>
                          <img src={fileUrl(c.image)} alt={c.name} className="w-full h-full object-cover" />
                          <div className="absolute inset-0 bg-velvet/50" />
                          <span className="absolute inset-0 flex items-center justify-center font-serif text-ivory text-sm px-2 text-center">{c.name}</span>
                          {f.menus.includes(c.name) && <span className="absolute top-2 right-2 w-6 h-6 rounded-full bg-gold flex items-center justify-center"><Check className="w-3.5 h-3.5 text-velvet" /></span>}
                        </button>
                      ))}
                    </div>
                  </div>)}

                  {step === 4 && (<div className="space-y-4" data-testid="qb-step-you">
                    <Overline>Almost there</Overline>
                    <h2 className="font-serif text-3xl text-burgundy">Your contact details</h2>
                    <input placeholder="Full name *" value={f.name} onChange={(e) => set("name", e.target.value)} className={inp} data-testid="qb-name" />
                    <input placeholder="Phone *" value={f.phone} onChange={(e) => set("phone", e.target.value)} className={inp} data-testid="qb-phone" />
                    <input type="email" placeholder="Email" value={f.email} onChange={(e) => set("email", e.target.value)} className={inp} data-testid="qb-email" />
                    {error && <p className="text-destructive text-sm">{error}</p>}
                  </div>)}
                </motion.div>
              </AnimatePresence>

              <div className="flex justify-between mt-12">
                <button onClick={prev} disabled={step === 0} className={`btn-outline-gold !text-charcoal !border-gold/40 ${step === 0 ? "opacity-0 pointer-events-none" : ""}`} data-testid="qb-prev"><ArrowLeft className="w-4 h-4" /> Back</button>
                {step < 4
                  ? <button onClick={next} className="btn-burgundy" data-testid="qb-next">Continue <ArrowRight className="w-4 h-4" /></button>
                  : <button onClick={submit} disabled={status === "loading" || !f.name || !f.phone} className="btn-gold" data-testid="qb-submit">{status === "loading" ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending…</> : <>Request Custom Quote <ArrowRight className="w-4 h-4" /></>}</button>}
              </div>
            </>
          )}
        </div>
      </section>
    </div>
  );
}
